import * as React from 'react';
import {
    List,
    ListItemText,
    Paper,
    Typography,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Button,
    Switch,
    FormControlLabel,
    Box,
    Chip,
} from '@mui/material';
import { ArticleOutlined, TerminalOutlined } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { serviceLocator } from './../services';
import { MainPadding } from '../components/MainPadding';
import { AppSection } from '../components/AppSection';

import ListItemButton from '@mui/material/ListItemButton';

export interface LogsState {
    currentLogKey: string | null;
    currentLogContent: string | null;
    logsKeys: string[];
    openDialog: boolean;
    prettyPrint: boolean;
}

const Pre = styled('pre')({
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
    width: '100%',
    margin: 0,
    fontFamily: '"JetBrains Mono", "SFMono-Regular", Menlo, monospace',
    fontSize: 12.5,
    lineHeight: 1.55,
    color: 'rgba(244, 246, 255, 0.86)',
});

const LogItem = styled('p')({
    fontFamily: '"JetBrains Mono", "SFMono-Regular", Menlo, monospace',
    fontSize: 12.5,
    lineHeight: 1.55,
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    padding: '8px 12px',
    margin: 0,
    color: 'rgba(244, 246, 255, 0.86)',
});

export class Logs extends React.Component<any, LogsState> {

    constructor(props: any) {
        super(props);
        this.state = { logsKeys: [], currentLogKey: null, currentLogContent: null, openDialog: false, prettyPrint: true };
    }

    public async componentDidMount() {
        const logsKeys = await serviceLocator.getLogService().listLogs();
        this.setState({ logsKeys });
    }

    private handlePrettyPrintChange = (e: any) => {
        this.setState({ prettyPrint: e.target.checked });
    };

    public render() {
        const logsKeys = this.state.logsKeys;
        return (
            <MainPadding>
                <AppSection section="logs" />

                <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                    <Box>
                        <Typography className="eyebrow" sx={{ color: 'text.secondary' }}>
                            Administration · runtime
                        </Typography>
                        <Typography
                            sx={{
                                mt: 0.75,
                                fontFamily: '"Inter Tight", "Inter", sans-serif',
                                fontWeight: 700,
                                letterSpacing: '-0.018em',
                                fontSize: { xs: 28, md: 34 },
                                lineHeight: 1.1,
                            }}
                        >
                            Application logs
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 600, lineHeight: 1.55 }}>
                            Server-side logs across the platform. Pick a stream on the left to inspect — newest entries surface first.
                        </Typography>
                    </Box>
                    <FormControlLabel
                        label="Pretty print"
                        control={
                            <Switch
                                checked={this.state.prettyPrint}
                                onChange={this.handlePrettyPrintChange}
                                color="secondary"
                            />
                        }
                        sx={{ '& .MuiFormControlLabel-label': { fontSize: 13, fontWeight: 500 } }}
                    />
                </Box>

                <Paper sx={{ overflow: 'hidden' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: 2.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            background: 'linear-gradient(180deg, rgba(5, 35, 114, 0.04) 0%, transparent 100%)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'linear-gradient(180deg, rgba(5, 35, 114, 0.10) 0%, rgba(5, 35, 114, 0.04) 100%)',
                                    color: 'primary.main',
                                    border: '1px solid rgba(5, 35, 114, 0.10)',
                                }}
                            >
                                <ArticleOutlined fontSize="small" />
                            </Box>
                            <Box>
                                <Typography className="eyebrow" sx={{ color: 'text.secondary' }}>
                                    Log streams
                                </Typography>
                                <Typography sx={{ fontWeight: 600, letterSpacing: '-0.005em' }}>
                                    {logsKeys.length} available
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {logsKeys.length === 0 && (
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                No log streams yet.
                            </Typography>
                        </Box>
                    )}

                    <List component="nav" disablePadding>
                        {logsKeys.map((logKey: any, i: number) => (
                            <ListItemButton
                                selected={this.state.currentLogKey === logKey}
                                key={`${logKey}`}
                                data-key={logKey}
                                onClick={this.handleItemClick}
                                divider={i < logsKeys.length - 1}
                                sx={{
                                    paddingInline: 2.5,
                                    paddingBlock: 1.5,
                                    borderRadius: 0,
                                    marginInline: 0,
                                    marginBottom: 0,
                                    '&::before': {
                                        content: '""',
                                        display: 'inline-block',
                                        width: 6,
                                        height: 6,
                                        borderRadius: '50%',
                                        background: 'rgba(5, 35, 114, 0.30)',
                                        marginRight: '12px',
                                        flexShrink: 0,
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={logKey}
                                    slotProps={{
                                        primary: {
                                            sx: {
                                                fontFamily: '"JetBrains Mono", monospace',
                                                fontSize: 13,
                                                fontWeight: 500,
                                                letterSpacing: '-0.002em',
                                            },
                                        },
                                    }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>

                <Dialog
                    open={this.state.openDialog}
                    scroll="paper"
                    fullScreen={true}
                    onClose={this.handleDialogClose}
                    slotProps={{
                        paper: {
                            sx: {
                                background: 'linear-gradient(180deg, #03124a 0%, #051a5e 100%)',
                                color: 'rgba(244, 246, 255, 0.86)',
                            },
                        },
                    }}
                >
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            background: 'rgba(0,0,0,0.18)',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                            color: '#ffffff',
                        }}
                    >
                        <TerminalOutlined sx={{ color: 'rgba(255, 180, 92, 0.92)' }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography className="eyebrow" sx={{ color: 'rgba(244, 246, 255, 0.45)' }}>
                                Log stream
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontWeight: 600,
                                    fontSize: 16,
                                    color: '#ffffff',
                                }}
                            >
                                {this.state.currentLogKey}
                            </Typography>
                        </Box>
                        <Chip
                            size="small"
                            label={this.state.prettyPrint ? 'Reverse · pretty' : 'Raw'}
                            sx={{
                                background: 'rgba(250, 135, 28, 0.12)',
                                color: '#ffb45c',
                                border: '1px solid rgba(250, 135, 28, 0.36)',
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: 10.5,
                                letterSpacing: '0.12em',
                                textTransform: 'uppercase',
                            }}
                        />
                    </DialogTitle>
                    <DialogContent sx={{ background: 'transparent', p: 2 }}>
                        {this.state.currentLogContent == null ? (
                            <Typography sx={{ color: 'rgba(244, 246, 255, 0.6)', textAlign: 'center', py: 6 }}>
                                Loading…
                            </Typography>
                        ) : this.state.prettyPrint ? (
                            <>
                                {(this.state.currentLogContent || '').split(/\n/).reverse().map((x, i) => (
                                    <LogItem key={i}>{x}</LogItem>
                                ))}
                            </>
                        ) : (
                            <Pre>{this.state.currentLogContent}</Pre>
                        )}
                    </DialogContent>
                    <DialogActions
                        sx={{
                            background: 'rgba(0,0,0,0.18)',
                            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                            padding: 2,
                        }}
                    >
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={this.handleDialogClose}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>
            </MainPadding>
        );
    }

    private handleDialogClose = async () => {
        this.setState({ openDialog: false });
    };

    private handleItemClick = async (e: any) => {
        const currentLogKey = e.currentTarget.dataset.key;
        this.setState({ currentLogKey, openDialog: true, currentLogContent: null });
        const currentLogContent = await serviceLocator.getLogService().getLogContent(currentLogKey);
        this.setState({ currentLogContent });
    };
}
